import {
  PendingPropertyIndicator,
  ProgressBarContainer,
  PropertyIndicatorContainer,
  PropertyIndicatorLabel
} from './utils';
import clsx from 'clsx';
import React from 'react';
import { isNil } from 'lodash';
import { LineItemIcon, MediaPlanIcon, PlatformBuyIcon } from '../../layout/nav-icons';
import { formatPercentage } from 'shared/src/domain/line-item/line-item-utils';

type Props = {
  allocated: number | null | undefined;
  total: number | null | undefined;
  isTotalsRow?: boolean;
  missingPlatform?: boolean;
  missingMediaPlans?: boolean;
};

export function PriceAllocatedIndicator({ allocated, total, isTotalsRow }: Props) {
  if (isTotalsRow) return null;
  if (!total) return <PendingPropertyIndicator label="Pending Price" />;
  if (isNil(allocated)) return <PendingPropertyIndicator label={<PendingLineItem />} />;
  const pct = calcProportion(allocated, total);
  return <ProgressIndicator percentage={pct} label={pctAllocatedLabel(pct)} />;
}

export function PriceAllocatedTooltip({ allocated, total }: Props) {
  if (isNil(allocated)) return null;
  if (!total) {
    return 'No campaign price in Zoho';
  }
  return <PercentAllocatedTooltip allocated={allocated} total={total} />;
}

export function LineItemsWithPlatformsIndicator({ allocated, total, isTotalsRow }: Props) {
  if (isTotalsRow) return null;
  if (isNil(allocated) || !total) return <PendingPropertyIndicator label={<PendingLineItem />} />;
  const pct = calcProportion(allocated, total);
  return <ProgressIndicator percentage={pct} label={countAllocatedLabel(pct)} />;
}

export function LineItemsWithPlatformsTooltip({ allocated, total }: Props) {
  if (isNil(allocated)) return null;
  if (!total) return 'Line items not complete';
  return <CountAllocatedTooltip allocated={allocated} total={total} label="line items" />;
}

export function LineItemsWithMediaPlansIndicator({
  allocated,
  total,
  missingMediaPlans,
  isTotalsRow
}: Props) {
  if (isTotalsRow) return null;
  if (missingMediaPlans) return <PendingPropertyIndicator label={<PendingMediaPlan />} />;
  if (isNil(allocated) || !total) return <PendingPropertyIndicator label={<PendingLineItem />} />;
  const pct = calcProportion(allocated, total);
  return <ProgressIndicator percentage={pct} label={countAllocatedLabel(pct)} />;
}

export function LineItemsWithMediaPlansTooltip({
  allocated,
  total,
  missingMediaPlans,
  isTotalsRow
}: Props) {
  if (isNil(allocated) || isTotalsRow) return null;
  if (missingMediaPlans) return 'No Media Plans';
  if (!total) return 'Line items not complete';
  return <CountAllocatedTooltip allocated={allocated} total={total} label="line items" />;
}

type IndicatorProps = {
  percentage: number;
  label: string;
};

function ProgressIndicator({ percentage, label }: IndicatorProps) {
  return (
    <PropertyIndicatorContainer className={bgColor(percentage)}>
      <PropertyIndicatorLabel className={txtColor(percentage)}>{label}</PropertyIndicatorLabel>
      <ProgressBar percentage={percentage} />
    </PropertyIndicatorContainer>
  );
}

export function PendingLineItem() {
  return (
    <div className="flex items-center gap-2">
      Pending
      <LineItemIcon />
    </div>
  );
}

export function PendingMediaPlan() {
  return (
    <div className="flex items-center gap-2">
      Pending
      <MediaPlanIcon />
    </div>
  );
}

export function PendingPlatformBuy() {
  return (
    <div className="flex items-center gap-2">
      Pending
      <PlatformBuyIcon />
    </div>
  );
}

function bgColor(percentage: number) {
  return percentage === 100 ? 'bg-lime-50' : 'bg-yellow-50';
}

function ProgressBar({ percentage }: { percentage: number }) {
  return percentage > 100 ? (
    <OverallocatedBar percentage={percentage} />
  ) : (
    <AllocatedBar percentage={percentage} />
  );
}

function OverallocatedBar({ percentage }: { percentage: number }) {
  const overage = ((percentage - 100) / percentage) * 100;
  const barColor = calcBarColor(percentage);
  return (
    <ProgressBarContainer>
      <div className="rounded-l-[3px] bg-gray-500" style={{ width: `${100 - overage}%` }} />
      <div
        className={clsx('progress-bar-over-allocated-shadow rounded-r-[3px]', barColor)}
        style={{ width: `${overage}%` }}
      />
    </ProgressBarContainer>
  );
}

function AllocatedBar({ percentage }: { percentage: number }) {
  const barColor = calcBarColor(percentage);
  return (
    <ProgressBarContainer>
      <div className={clsx('h-1 rounded-[3px]', barColor)} style={{ width: `${percentage}%` }} />
    </ProgressBarContainer>
  );
}

function calcBarColor(percentage: number) {
  return percentage === 100
    ? 'bg-linear-to-r from-[#A3E635] to-[#65A30D]'
    : 'bg-linear-to-r from-[#FBBF24] to-[#CA8A04]';
}

function txtColor(percentage: number) {
  return percentage === 100 ? 'text-lime-700' : 'text-yellow-700';
}

export function pctAllocatedLabel(percentage: number) {
  if (percentage === 0) return 'Not started';
  else if (percentage === 100) return 'Complete';
  else if (percentage > 100) return 'Over-allocated';
  else return 'Partial';
}

export function countAllocatedLabel(percentage: number | null | undefined) {
  if (isNil(percentage) || percentage === 0) return 'None';
  else if (percentage === 100) return 'All';
  else return 'Some';
}

function calcProportion(allocated: number | null | undefined, total: number) {
  if (isNil(allocated)) return 0;
  return (allocated / total) * 100;
}

type TooltipProps = {
  allocated: number;
  total: number;
  label?: string;
};
export function PercentAllocatedTooltip({ allocated, total }: TooltipProps) {
  return (
    <div className="flex gap-1">
      <div className="font-semibold">{formatPercentage(allocated / total)}</div>
      allocated
    </div>
  );
}

export function CountAllocatedTooltip({ allocated, total, label }: TooltipProps) {
  return (
    <div className="flex gap-1">
      <div className="font-semibold">{allocated || 0}</div>
      of {total} {label}
    </div>
  );
}
